export default function HowToPlay({ onClose }) {
  return (
    <div style={{ padding: '1rem', fontFamily: 'sans-serif', maxWidth: 540, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ fontSize: 20, fontWeight: 500, margin: 0, color: '#1a1a1a' }}>♠ How to play ♠</h2>
        {onClose && <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#CC0000' }}>✕</button>}
      </div>

      <Section title="♥ Setup" color="#CC0000">
        <Step n="1">Each player is dealt <strong>4 cards face-down</strong> in a 2×2 grid</Step>
        <Step n="2">Peek at your <strong>bottom 2 cards</strong> before play begins</Step>
        <Step n="3">First player draws from the pile and play begins</Step>
      </Section>

      <Section title="♣ Goal" color="#1a1a1a">
        <p style={{ margin: '0 0 10px', fontSize: 14, color: '#444', lineHeight: 1.7 }}>
          Finish every round with the <strong>lowest score</strong>. The game ends when any player reaches the max score. The player with the lowest total wins!
        </p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Badge text="Lowest score wins" bg="#E1F5EE" color="#0F6E56" />
          <Badge text="Default max: 100 pts" bg="#FAEEDA" color="#854F0B" />
        </div>
      </Section>

      <Section title="♦ Card values" color="#CC0000">
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          <CardVal label="K" pts="0 pts" sub="Best!" highlight />
          <CardVal label="A" pts="1 pt" />
          <CardVal label="2–9" pts="face value" />
          <CardVal label="10" pts="10 pts" red />
          <CardVal label="J Q" pts="10 pts" red />
          <CardVal label="pair" pts="= 0 pts!" purple />
        </div>
        <div style={{ background: '#f9f9f9', border: '1px solid #C9A84C', borderRadius: 8, padding: '10px 12px', fontSize: 13, color: '#444' }}>
          ✨ Matching pairs cancel each other out — two 9s = 0 points!
        </div>
      </Section>

      <Section title="♠ Your turn" color="#1a1a1a">
        <TurnOption
          title="Draw from the pile"
          desc="Then: flip a face-down card (drawn card discarded), swap the drawn card with a face-down card, or discard drawn card and flip one of yours."
        />
        <TurnOption
          title="Take the top discard"
          desc="Swap the taken card with one of your face-down cards. The replaced card goes to the discard pile."
        />
      </Section>

      <Section title="♥ Important rules" color="#CC0000">
        <Rule>Peek at your bottom 2 cards anytime using the <strong>👁 peek</strong> button</Rule>
        <Rule>Once a card is flipped face-up it <strong>cannot be swapped out</strong></Rule>
        <Rule>Every turn must end with one card being flipped</Rule>
        <Rule>Matching pairs cancel out and score <strong>0 points</strong></Rule>
      </Section>

      <Section title="♣ Winning" color="#1a1a1a">
        <Step n="1">Round ends when all players have all 4 cards face-up</Step>
        <Step n="2">Scores are added up — pairs cancel to 0!</Step>
        <Step n="3">First player to reach max score is eliminated</Step>
        <Step n="4">Player with the <strong>lowest score</strong> wins!</Step>
      </Section>

      <button onClick={onClose} style={{
        background: '#1a1a1a', color: '#C9A84C', border: 'none', borderRadius: 8,
        padding: '12px 20px', fontSize: 14, fontWeight: 500, cursor: 'pointer',
        width: '100%', marginTop: 8, letterSpacing: 1,
      }}>
        Got it — let's play! ♠ ♥ ♦ ♣
      </button>
    </div>
  );
}

function Section({ title, color, children }) {
  return (
    <div style={{
      background: '#fff', border: '0.5px solid #e0e0e0', borderLeft: `3px solid ${color}`,
      borderRadius: 10, padding: '1rem 1.25rem', marginBottom: 12,
    }}>
      <div style={{ fontSize: 15, fontWeight: 500, color, marginBottom: 12 }}>{title}</div>
      {children}
    </div>
  );
}

function Step({ n, children }) {
  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 10 }}>
      <div style={{
        minWidth: 26, height: 26, borderRadius: '50%', background: '#E1F5EE',
        color: '#0F6E56', display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 13, fontWeight: 500, flexShrink: 0,
      }}>{n}</div>
      <div style={{ fontSize: 14, color: '#444', lineHeight: 1.6, paddingTop: 4 }}>{children}</div>
    </div>
  );
}

function Rule({ children }) {
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 8 }}>
      <div style={{ minWidth: 6, height: 6, borderRadius: '50%', background: '#CC0000', marginTop: 8, flexShrink: 0 }} />
      <div style={{ fontSize: 14, color: '#444', lineHeight: 1.6 }}>{children}</div>
    </div>
  );
}

function Badge({ text, bg, color }) {
  return (
    <span style={{ display: 'inline-block', fontSize: 12, padding: '3px 10px', borderRadius: 6, fontWeight: 500, background: bg, color }}>
      {text}
    </span>
  );
}

function CardVal({ label, pts, sub, highlight, red, purple }) {
  const bg = highlight ? '#E1F5EE' : purple ? '#EEEDFE' : '#fff';
  const borderColor = highlight ? '#5DCAA5' : purple ? '#AFA9EC' : '#e0e0e0';
  const valColor = red ? '#CC0000' : purple ? '#3C3489' : '#1a1a1a';
  const ptsColor = highlight ? '#0F6E56' : purple ? '#3C3489' : '#888';
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      borderRadius: 8, border: `1px solid ${borderColor}`, background: bg,
      padding: '10px 8px', minWidth: 60,
    }}>
      <span style={{ fontSize: 18, fontWeight: 500, color: valColor }}>{label}</span>
      <span style={{ fontSize: 11, color: ptsColor, marginTop: 4, fontWeight: highlight || purple ? 500 : 400 }}>{pts}</span>
      {sub && <span style={{ fontSize: 10, color: '#0F6E56', marginTop: 2 }}>{sub}</span>}
    </div>
  );
}

function TurnOption({ title, desc }) {
  return (
    <div style={{
      padding: '10px 12px', borderRadius: 8, border: '0.5px solid #e0e0e0',
      marginBottom: 8, background: '#fafafa',
    }}>
      <div style={{ fontSize: 14, fontWeight: 500, color: '#1a1a1a', marginBottom: 4 }}>▶ {title}</div>
      <div style={{ fontSize: 13, color: '#666', lineHeight: 1.5 }}>{desc}</div>
    </div>
  );
}
